// ---------------------------------------------------------------------------
// Judge-validation wire shape + mapper.
//
// `GET /grader-runs/{id}?type=judge-validation` returns a `GraderRunResponse`
// whose polymorphic `results` is a `JudgeValidationJobResults` — a thin .NET
// envelope (`workerApiVersion` + `validation`) wrapping the verbatim WorkerPy
// `ValidateJudgeResponse`. `mapValidationRun` flattens that nested
// `validation` up so the client-facing model exposes the inner report
// directly via `results.results`.
//
// Lives in its own module because the validation payload is ~250 lines of
// per-category type definitions on top of a non-trivial flatten — keeping it
// next to the rest of the API service would crowd that file out. The wire
// types are local to this module; consumers should depend on the domain
// types in `graderRun.interface.ts` instead.
// ---------------------------------------------------------------------------

import type {
  IssueSeverity,
  JobStatus,
  JudgeValidationOverallStatus,
  JudgeValidationResults,
  JudgeValidationRun,
  ValidationResultsByCategory,
  ValidationStats,
} from './graderRun.interface';

// ─── Wire types ────────────────────────────────────────────────────────────

interface WireValidationStats {
  examplesCount: number;
  durationMs: number;
}

/** Common fields on every per-category issue, before category-specific extras. */
interface WireIssueBase {
  severity: string;
  category: string;
  message: string;
  suggestion?: string;
  details?: string;
  affectedItems?: number[];
}

type WireConsistencyIssue = WireIssueBase;

interface WireContradictionIssue extends WireIssueBase {
  judgeSnippet: string;
  systemSnippet: string;
  impact: string;
}

interface WireSemanticQuickFix {
  action: string;
  description: string;
  specificSteps: string[];
  priority: string;
  codeSnippet?: string;
}

interface WireSemanticIssue extends WireIssueBase {
  confidence?: number;
  metadata?: Record<string, unknown>;
  quickFix?: WireSemanticQuickFix;
}

interface WireDataQualityResult {
  status: string;
  issues?: WireConsistencyIssue[];
  skipReason?: string;
}

interface WireRequirementsResult {
  status: string;
  issues?: WireContradictionIssue[];
  summary?: string;
  skipReason?: string;
}

interface WireExtractedCriterion {
  name: string;
  description: string;
  weight: number;
}

interface WireMlSemanticResult {
  status: string;
  issues?: WireSemanticIssue[];
  consistencyRate?: number;
  extractedCriteria?: WireExtractedCriterion[];
  diversityScores?: Record<string, number>;
  boundaryExamples?: number[];
  coveragePerScoreLevel?: Record<string, number>;
  skipReason?: string;
}

interface WireExampleAlignmentStats {
  checked: number;
  passed: number;
}

interface WireChecksPassed {
  contradiction: boolean;
  clarity: boolean;
  exampleAlignment: boolean;
  judgeTaskAlignment: boolean;
  scoreScaleValidity: boolean;
  exampleAlignmentStats?: WireExampleAlignmentStats;
}

interface WireScoreScaleAnalysis {
  scoreDistribution: Record<string, number>;
  unusedScores: string[];
  suggestedScale?: string;
  binaryDecompositionRecommended?: boolean;
  rubricClarityRating?: string;
}

interface WireRubricClarityResult {
  status: string;
  issues?: WireConsistencyIssue[];
  checksPassed?: WireChecksPassed;
  scoreScaleAnalysis?: WireScoreScaleAnalysis;
  skipReason?: string;
}

interface WireValidationResultsByCategory {
  dataQuality: WireDataQualityResult;
  requirements: WireRequirementsResult;
  mlSemantic: WireMlSemanticResult;
  rubricClarity: WireRubricClarityResult;
}

interface WireValidationBody {
  status: string;
  stats: WireValidationStats;
  results: WireValidationResultsByCategory;
}

interface WireJudgeValidationResults {
  workerApiVersion?: string;
  validation: WireValidationBody;
}

/** Wire shape of `GET /grader-runs/{id}?type=judge-validation`. */
export interface WireValidationRun {
  id: string;
  type: string;
  status: JobStatus;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  results?: WireJudgeValidationResults;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

// Severity / status come back as strings; we narrow to the documented enum
// but pass anything unknown through unchanged so a server addition doesn't
// crash the UI — the renderer treats unrecognised values as info-grade.
function asSeverity(s: string): IssueSeverity {
  return s as IssueSeverity;
}

function asOverallStatus(s: string): JudgeValidationOverallStatus {
  return s as JudgeValidationOverallStatus;
}

function mapIssueBase(i: WireIssueBase) {
  return {
    severity: asSeverity(i.severity),
    category: i.category,
    message: i.message,
    suggestion: i.suggestion,
    details: i.details,
    affectedItems: i.affectedItems ?? [],
  };
}

function mapResultsByCategory(
  r: WireValidationResultsByCategory,
): ValidationResultsByCategory {
  return {
    dataQuality: {
      status: asOverallStatus(r.dataQuality.status),
      issues: (r.dataQuality.issues ?? []).map((i) => mapIssueBase(i)),
      skipReason: r.dataQuality.skipReason,
    },
    requirements: {
      status: asOverallStatus(r.requirements.status),
      issues: (r.requirements.issues ?? []).map((i) => ({
        ...mapIssueBase(i),
        judgeSnippet: i.judgeSnippet,
        systemSnippet: i.systemSnippet,
        impact: i.impact,
      })),
      summary: r.requirements.summary,
      skipReason: r.requirements.skipReason,
    },
    mlSemantic: {
      status: asOverallStatus(r.mlSemantic.status),
      issues: (r.mlSemantic.issues ?? []).map((i) => ({
        ...mapIssueBase(i),
        confidence: i.confidence ?? 1.0,
        metadata: i.metadata ?? {},
        quickFix: i.quickFix
          ? {
              action: i.quickFix.action,
              description: i.quickFix.description,
              specificSteps: i.quickFix.specificSteps,
              priority: i.quickFix.priority,
              codeSnippet: i.quickFix.codeSnippet,
            }
          : undefined,
      })),
      consistencyRate: r.mlSemantic.consistencyRate,
      extractedCriteria: r.mlSemantic.extractedCriteria ?? [],
      diversityScores: r.mlSemantic.diversityScores ?? {},
      boundaryExamples: r.mlSemantic.boundaryExamples ?? [],
      coveragePerScoreLevel: r.mlSemantic.coveragePerScoreLevel,
      skipReason: r.mlSemantic.skipReason,
    },
    rubricClarity: {
      status: asOverallStatus(r.rubricClarity.status),
      issues: (r.rubricClarity.issues ?? []).map((i) => mapIssueBase(i)),
      checksPassed: r.rubricClarity.checksPassed
        ? {
            contradiction: r.rubricClarity.checksPassed.contradiction,
            clarity: r.rubricClarity.checksPassed.clarity,
            exampleAlignment: r.rubricClarity.checksPassed.exampleAlignment,
            judgeTaskAlignment: r.rubricClarity.checksPassed.judgeTaskAlignment,
            scoreScaleValidity: r.rubricClarity.checksPassed.scoreScaleValidity,
            exampleAlignmentStats:
              r.rubricClarity.checksPassed.exampleAlignmentStats,
          }
        : undefined,
      scoreScaleAnalysis: r.rubricClarity.scoreScaleAnalysis
        ? {
            scoreDistribution:
              r.rubricClarity.scoreScaleAnalysis.scoreDistribution,
            unusedScores: r.rubricClarity.scoreScaleAnalysis.unusedScores,
            suggestedScale: r.rubricClarity.scoreScaleAnalysis.suggestedScale,
            binaryDecompositionRecommended:
              r.rubricClarity.scoreScaleAnalysis
                .binaryDecompositionRecommended ?? false,
            rubricClarityRating:
              r.rubricClarity.scoreScaleAnalysis.rubricClarityRating,
          }
        : undefined,
      skipReason: r.rubricClarity.skipReason,
    },
  };
}

function mapValidationResults(
  r: WireJudgeValidationResults,
): JudgeValidationResults {
  const stats: ValidationStats = {
    examplesCount: r.validation.stats.examplesCount,
    durationMs: r.validation.stats.durationMs,
  };
  return {
    status: asOverallStatus(r.validation.status),
    stats,
    results: mapResultsByCategory(r.validation.results),
    workerApiVersion: r.workerApiVersion,
  };
}

/** Map the wire payload to the client-facing `JudgeValidationRun`. */
export function mapValidationRun(r: WireValidationRun): JudgeValidationRun {
  return {
    id: r.id,
    type: r.type,
    status: r.status,
    createdAt: r.createdAt,
    startedAt: r.startedAt,
    completedAt: r.completedAt,
    error: r.error,
    results: r.results ? mapValidationResults(r.results) : undefined,
  };
}
